import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import StepActions from './StepActions';
import { hasAvailableSlotsForUrgency } from '../scheduleAvailability';

const UrgencyStep = ({ data, onUpdate, onNext, onBack, isActive = true, showBack = true }) => {
  const [emergencyPercentages, setEmergencyPercentages] = useState({
    priority_percentage: 20,
    emergency_percentage: 50
  });
  
  const allUrgencyOptions = [
    {
      id: 'today',
      name: 'Aujourd\'hui',
      description: 'Intervention dans la journée',
      icon: 'Clock',
      priceMultiplier: 1.2,
      availability: 'Créneaux disponibles',
      badge: 'Prioritaire',
      badgeColor: 'warning',
    },
    {
      id: 'within-hour',
      name: 'Sous 2h',
      description: 'Intervention urgente sous 2 heures',
      icon: 'Zap',
      priceMultiplier: 1.5,
      availability: 'Sous réserve de disponibilité',
      badge: 'Urgent',
      badgeColor: 'error',
    },
    {
      id: 'tomorrow',
      name: 'Choisir une date',
      description: 'Fixez vous-même la date et l\'heure d\'intervention',
      icon: 'Calendar',
      priceMultiplier: 1.0,
      availability: 'Meilleure disponibilité',
      badge: 'Standard',
      badgeColor: 'success',
    },
  ];

  const standardOption = allUrgencyOptions.find(opt => opt.priceMultiplier === 1.0);

  const [selectedUrgency, setSelectedUrgency] = useState(data?.urgency || standardOption);
  const [error, setError] = useState('');
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [scheduleContext, setScheduleContext] = useState({
    technicians: [],
    appointments: [],
    timeOffEvents: [],
  });

  const durationMinutes = data?.service?.estimatedDuration || 60;

  const isUrgencyAvailable = (urgencyId) => {
    if (urgencyId === 'tomorrow') return true;
    return hasAvailableSlotsForUrgency(urgencyId, {
      durationMinutes,
      ...scheduleContext,
    });
  };

  const otherOptions = allUrgencyOptions
    .filter(opt => opt.priceMultiplier !== 1.0)
    .filter(opt => isUrgencyAvailable(opt.id));

  useEffect(() => {
    const fetchScheduleContext = async () => {
      setAvailabilityLoading(true);
      try {
        const [
          { data: techniciansData, error: techniciansError },
          { data: appointmentsData, error: appointmentsError },
          { data: timeoffData, error: timeoffError },
        ] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('id, full_name, specialty, is_active')
            .eq('role', 'technicien')
            .eq('is_active', true),
          supabase
            .from('appointments')
            .select('scheduled_date, scheduled_time_start, scheduled_time_end, technician')
            .not('scheduled_date', 'is', null)
            .not('scheduled_time_start', 'is', null)
            .not('scheduled_time_end', 'is', null),
          supabase.from('technician_timeoff').select('*'),
        ]);

        if (techniciansError) throw techniciansError;
        if (appointmentsError) throw appointmentsError;
        if (timeoffError) throw timeoffError;

        setScheduleContext({
          technicians: techniciansData || [],
          appointments: appointmentsData || [],
          timeOffEvents: timeoffData || [],
        });
      } catch (fetchError) {
        console.error('Error fetching urgency availability:', fetchError);
        setScheduleContext({
          technicians: [],
          appointments: [],
          timeOffEvents: [],
        });
      } finally {
        setAvailabilityLoading(false);
      }
    };

    fetchScheduleContext();
  }, []);

  useEffect(() => {
    if (availabilityLoading || !standardOption) return;

    const currentId = selectedUrgency?.id;
    if (!currentId || currentId === 'tomorrow') return;

    const stillAvailable = hasAvailableSlotsForUrgency(currentId, {
      durationMinutes,
      ...scheduleContext,
    });

    if (!stillAvailable) {
      setSelectedUrgency(standardOption);
      if (data?.urgency?.id && data.urgency.id !== 'tomorrow') {
        onUpdate({ urgency: standardOption });
      }
    }
  }, [availabilityLoading, scheduleContext, durationMinutes, standardOption?.id]);

  useEffect(() => {
    if (!data?.urgency && standardOption && !selectedUrgency) {
      setSelectedUrgency(standardOption);
    }

    const fetchEmergencyPercentages = async () => {
      try {
        const { data: percentages, error } = await supabase
          .from('emergency_percentage')
          .select('priority_percentage, emergency_percentage')
          .single();
        
        if (error) throw error;
        
        if (percentages) {
          setEmergencyPercentages({
            priority_percentage: percentages.priority_percentage || 20,
            emergency_percentage: percentages.emergency_percentage || 50
          });
        }
      } catch (error) {
        console.error('Error fetching emergency percentages:', error);
      }
    };

    fetchEmergencyPercentages();
  }, []);

  const basePrice = data?.service?.basePrice || data?.service?.price || 0;

  const pricepriority = ((emergencyPercentages.priority_percentage * basePrice) / 100) + basePrice;
  const priceuregence = ((emergencyPercentages.emergency_percentage * basePrice) / 100) + basePrice;

  const handleUrgencySelect = (urgency) => {
    setSelectedUrgency(urgency);
    setError('');
    handleSubmit(urgency);
  };

  const handleSubmit = (urgencyParam) => {
    const urgencyToSubmit = urgencyParam || selectedUrgency;

    if (!urgencyToSubmit) {
      setError("Veuillez sélectionner un niveau d'urgence pour continuer");
      return;
    }

    onUpdate({
      urgency: urgencyToSubmit,
    });
    onNext(true);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 lg:p-8">
        <div className="flex items-start gap-3 sm:gap-4 ">
          
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-1 sm:mb-2">
              Quand souhaitez-vous que nous intervenions ?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Le tarif varie selon l'urgence de votre demande
            </p>
          </div>
        </div>
  {/* <StepActions isActive={isActive} className="flex justify-between pt-6 pb-6 sm:pt-8">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
        </StepActions> */}
  <div className="flex justify-between pt-6  border-t border-border">
          
        </div>
        <div className="space-y-4">
          {/* Créneau standard */}
          {standardOption && (
            <button
              onClick={() => handleUrgencySelect(standardOption)}
              className={`w-full text-left p-4 sm:p-6 rounded-lg border-2 transition-all duration-200 ${
                selectedUrgency?.id === standardOption.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 bg-card'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      selectedUrgency?.id === standardOption.id ? 'bg-primary/20' : 'bg-muted'
                    }`}
                  >
                    <Icon
                      name={standardOption.icon}
                      size={20}
                      className="sm:w-6 sm:h-6"
                      color={selectedUrgency?.id === standardOption.id ? 'var(--color-primary)' : 'currentColor'}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1 sm:mb-2">
                      <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">
                        {standardOption.name}
                      </h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success whitespace-nowrap">
                        {standardOption.badge}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Icon name="Info" size={14} className="flex-shrink-0" />
                      <span className="truncate">{standardOption.availability}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                  {basePrice > 0 ? (
                    <>
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-lg sm:text-2xl font-bold text-foreground whitespace-nowrap">
                          {basePrice.toFixed(2)}
                        </span>
                        <span className="text-base sm:text-2xl font-bold text-foreground">€</span>
                      </div>
                      <div className="text-xs text-muted-foreground text-right">HT</div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground whitespace-nowrap">Sur devis</div>
                  )}
                  {selectedUrgency?.id === standardOption.id && (
                    <Icon name="CheckCircle" size={20} className="sm:w-6 sm:h-6 text-primary mt-1" />
                  )}
                </div>
              </div>
            </button>
          )}

          {!availabilityLoading && otherOptions.length === 0 && (
            <p className="pt-4 border-t border-border text-xs sm:text-sm text-muted-foreground">
              Aucune option urgente disponible pour le moment (créneaux complets ou hors horaires d&apos;intervention 9h-18h).
            </p>
          )}

          {/* Autres créneaux */}
          {otherOptions.length > 0 && (
            <div className="pt-4 border-t border-border">
              <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-3">
                Autres créneaux disponibles
              </p>
              <div className="space-y-2">
                {otherOptions.map((urgency) => {
                  return (
                    <button
                      key={urgency.id}
                      onClick={() => handleUrgencySelect(urgency)}
                      className={`w-full text-left p-4 sm:p-6 rounded-lg border-2 transition-all duration-200 ${
                        selectedUrgency?.id === urgency.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 bg-card'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <div
                            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              selectedUrgency?.id === urgency.id ? 'bg-primary/20' : 'bg-muted'
                            }`}
                          >
                            <Icon
                              name={urgency.icon}
                              size={16}
                              className="sm:w-[18px] sm:h-[18px]"
                              color={selectedUrgency?.id === urgency.id ? 'var(--color-primary)' : 'currentColor'}
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                              <h4 className="text-xs sm:text-sm font-medium text-foreground truncate">
                                {urgency.name}
                              </h4>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap ${
                                  urgency.badgeColor === 'error' ? 'bg-error/10 text-error'
                                    : urgency.badgeColor === 'warning' ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                                }`}
                              >
                                {urgency.badge}
                              </span>
                            </div>
                            
                          </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                          {urgency.id === 'within-hour' && (
                            <div className="flex items-baseline gap-0.5">
                              {data?.service?.PricingModel === 'prix fixe' ? (
                                <>
                                  <span className="text-xs sm:text-sm font-semibold text-error whitespace-nowrap">
                                    {priceuregence.toFixed(2)}
                                  </span>
                                  <span className="text-[10px] sm:text-xs text-error">€ HT</span>
                                </>
                              ) : (
                                <span className="text-xs sm:text-sm font-semibold text-error whitespace-nowrap">
                                  + 50%
                                </span>
                              )}
                            </div>
                          )}

                          {urgency.id === 'today' && (
                            <div className="flex items-baseline gap-0.5">
                              {data?.service?.PricingModel === 'prix fixe' ? (
                                <>
                                  <span className="text-xs sm:text-sm font-semibold text-warning whitespace-nowrap">
                                    {pricepriority.toFixed(2)}
                                  </span>
                                  <span className="text-[10px] sm:text-xs text-warning">€ HT</span>
                                </>
                              ) : (
                                <span className="text-xs sm:text-sm font-semibold text-warning whitespace-nowrap">
                                  + 20%
                                </span>
                              )}
                            </div>
                          )}

                          {selectedUrgency?.id === urgency.id && (
                            <Icon name="CheckCircle" size={16} className="sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 sm:mt-6 flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-error/10 border border-error/20 rounded-lg">
            <Icon name="AlertCircle" size={18} className="sm:w-5 sm:h-5 text-error flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-error">{error}</span>
          </div>
        )}

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-6 sm:pt-8 mt-6 sm:mt-8 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
        </StepActions>
      </div>
    </div>
  ); 
};

export default UrgencyStep;